import { JIRA_BASE_URL } from "./constants";

// Jira issue keys look like PROJECT-123. Uppercase only: matching lowercase
// would turn branch names like renovate/node-18.x into bogus keys. Strings
// such as UTF-8 still match; a stray badge just links to a missing ticket.
const TICKET_KEY_PATTERN =
	/(?<![A-Za-z0-9])([A-Z][A-Z0-9]+-\d+)(?![A-Za-z0-9])/g;

const keysIn = (text) =>
	text ? [...text.matchAll(TICKET_KEY_PATTERN)].map((m) => m[1]) : [];

/**
 * Jira ticket keys referenced by an MR, de-duplicated, title keys first and
 * then source-branch keys. The first entry is the MR's primary ticket.
 */
export const getTicketKeys = (mr) => [
	...new Set([...keysIn(mr?.title), ...keysIn(mr?.sourceBranch)]),
];

/**
 * The query itself when it is a whole ticket key (as set by clicking a
 * badge), so search can match that ticket exactly instead of as a substring
 * that would also catch NET-12 for NET-1. Otherwise null.
 */
export const asTicketKey = (query) => {
	const trimmed = query?.trim() ?? "";
	return /^[A-Z][A-Z0-9]+-\d+$/.test(trimmed) ? trimmed : null;
};

export const jiraBrowseUrl = (key) =>
	`${JIRA_BASE_URL}/browse/${encodeURIComponent(key)}`;

const compareTicketKeys = (a, b) => a.localeCompare(b, "en", { numeric: true });

/**
 * Groups MRs by their primary ticket key, matching the badge each card shows.
 * Returns lanes of { key, mrs }, busiest ticket first, then by key; MRs that
 * reference no ticket collect in a final lane whose key is null.
 */
export const groupByPrimaryTicket = (mrs) => {
	const groups = new Map();
	for (const mr of mrs) {
		const key = getTicketKeys(mr)[0] ?? null;
		if (!groups.has(key)) groups.set(key, { key, mrs: [] });
		groups.get(key).mrs.push(mr);
	}
	const unticketed = groups.get(null);
	groups.delete(null);
	const lanes = [...groups.values()].sort(
		(a, b) => b.mrs.length - a.mrs.length || compareTicketKeys(a.key, b.key),
	);
	return unticketed ? [...lanes, unticketed] : lanes;
};
